import { HIGH_EMISSION_SECTORS } from './taxonomy'
import type { ApplicabilityState, CompanyProfile } from './types'

export type ApplicabilityRule = {
  id: string
  recordId: string
  when: (profile: CompanyProfile) => boolean
  state: ApplicabilityState
  because: string
  triggeredBy: string[]
  unverified: string[]
}

const EMIRATES = new Set(['abu_dhabi', 'dubai', 'sharjah', 'ajman', 'uaq', 'rak', 'fujairah'])

export function uaePresence(profile: CompanyProfile): 'yes' | 'no' | 'not_sure' {
  const inUae = profile.emirates.some((id) => EMIRATES.has(id))
  if (inUae) return 'yes'
  if (profile.emirates.includes('none')) return 'no'
  return 'not_sure'
}

export function emptyProfile(): CompanyProfile {
  return {
    emirates: [],
    establishment: '',
    sector: '',
    activities: [],
    listing: '',
    financial: '',
    size: '',
    ipo: '',
    exportEu: '',
    customerRequests: '',
    lenderRequests: '',
    multiFacility: '',
    officialNotice: '',
    ghgInventory: '',
  }
}

const OUTDOOR_SECTORS = new Set(['construction', 'agriculture', 'oil_gas', 'logistics'])
const INDUSTRIAL_SECTORS = new Set(['manufacturing', 'construction', 'energy', 'oil_gas', 'waste'])

function hasActivity(profile: CompanyProfile, id: string): boolean {
  return profile.activities.includes(id)
}

function listedAdx(profile: CompanyProfile): boolean {
  return profile.listing === 'adx' || profile.listing === 'both_exchanges'
}

function listedDfm(profile: CompanyProfile): boolean {
  return profile.listing === 'dfm' || profile.listing === 'both_exchanges'
}

/**
 * Documented, conservative rules.
 * Company size is collected and deliberately unused: none of the verified instruments uses headcount as a threshold.
 * "Not sure" produces further verification rather than a finding of non-applicability.
 */
export const APPLICABILITY_RULES: ApplicabilityRule[] = [
  {
    id: 'fdl11-presence',
    recordId: 'uae-fdl-11-2024',
    when: (p) => uaePresence(p) === 'yes',
    state: 'potentially_relevant',
    because:
      'An emirate of operation was selected. Federal Decree-Law No. 11 of 2024 applies to Sources in the State, including free zones. That is not a determination that this entity must file periodic emissions reports.',
    triggeredBy: ['Emirate of operation'],
    unverified: [
      'Whether activities release greenhouse gases and the entity is a Source',
      'Whether the Ministry and the competent authority have determined the entity under Article 6',
    ],
  },
  {
    id: 'fdl11-unsure',
    recordId: 'uae-fdl-11-2024',
    when: (p) => uaePresence(p) === 'not_sure',
    state: 'further_verification_required',
    because: 'Place of operation is not confirmed, so the Decree-Law cannot be ruled out from the answers given.',
    triggeredBy: ['Emirate of operation: Not sure or unanswered'],
    unverified: ['Whether the entity operates in the UAE'],
  },
  {
    id: 'fdl11-notice',
    recordId: 'uae-fdl-11-2024',
    when: (p) => p.officialNotice === 'yes',
    state: 'further_verification_required',
    because:
      'An official notice was indicated. The notice may or may not be an Article 6 determination. This tool cannot read or validate it.',
    triggeredBy: ['Official reporting notice'],
    unverified: ['Issuing authority, legal basis and any deadline in the notice'],
  },
  {
    id: 'fdl11-notice-unsure',
    recordId: 'uae-fdl-11-2024',
    when: (p) => uaePresence(p) === 'yes' && p.officialNotice === 'not_sure',
    state: 'further_verification_required',
    because: 'It is unclear whether a reporting notice has been received. Do not treat uncertainty as “not designated”.',
    triggeredBy: ['Official reporting notice: Not sure'],
    unverified: ['Whether any competent authority has written to the entity'],
  },
  {
    id: 'cab67-threshold-clues',
    recordId: 'uae-cab-67-2024',
    when: (p) =>
      uaePresence(p) === 'yes' &&
      ((HIGH_EMISSION_SECTORS as readonly string[]).includes(p.sector) ||
        p.multiFacility === 'yes' ||
        hasActivity(p, 'high_energy') ||
        hasActivity(p, 'industrial_operations')),
    state: 'potentially_relevant',
    because:
      'UAE operations plus a higher-emission sector, multiple facilities, or industrial or high-energy activity were indicated. Cabinet Resolution No. 67 of 2024 applies at or above 0.5 million tCO2e a year of Scope 1 and Scope 2, or if the entity joins voluntarily. Those facts were not established by the profile.',
    triggeredBy: ['UAE presence', 'Sector, facilities or energy-intensive activity'],
    unverified: ['Actual Scope 1 and Scope 2 total against 0.5 million tCO2e', 'Whether the entity has opted into the register'],
  },
  {
    id: 'cab67-notice',
    recordId: 'uae-cab-67-2024',
    when: (p) => p.officialNotice === 'yes',
    state: 'further_verification_required',
    because: 'An official notice was indicated. It might relate to the carbon register or to a different regime. Review the document.',
    triggeredBy: ['Official reporting notice'],
    unverified: ['Whether the notice is a register or MRV determination under Resolution No. 67 of 2024'],
  },
  {
    id: 'cab67-unsure-place',
    recordId: 'uae-cab-67-2024',
    when: (p) => uaePresence(p) === 'not_sure' && ((HIGH_EMISSION_SECTORS as readonly string[]).includes(p.sector) || p.sector === 'not_sure'),
    state: 'further_verification_required',
    because: 'Location is uncertain and the sector is either emissions-intensive or unknown. The resolution cannot be excluded.',
    triggeredBy: ['Emirate of operation: Not sure', 'Sector'],
    unverified: ['UAE presence and the Scope 1 and Scope 2 threshold'],
  },
  {
    id: 'waste-presence',
    recordId: 'uae-fl-12-2018-waste',
    when: (p) => uaePresence(p) === 'yes',
    state: 'potentially_relevant',
    because:
      'Federal Law No. 12 of 2018 applies to waste in the State, including free zones. Selecting an emirate shows that waste may be generated. It does not show which permit applies.',
    triggeredBy: ['Emirate of operation'],
    unverified: ['Waste streams and the competent authority’s permit rules'],
  },
  {
    id: 'waste-handling',
    recordId: 'uae-fl-12-2018-waste',
    when: (p) => uaePresence(p) === 'yes' && (hasActivity(p, 'waste_handling') || p.sector === 'waste'),
    state: 'likely_relevant',
    because: 'Waste handling was indicated, or the sector is waste services, inside the UAE. The law regulates waste through to disposal. The specific licence still depends on the stream and the authority.',
    triggeredBy: ['Waste activity or waste-sector selection'],
    unverified: ['Which waste streams require a permit at each site'],
  },
  {
    id: 'waste-unsure',
    recordId: 'uae-fl-12-2018-waste',
    when: (p) => uaePresence(p) === 'not_sure',
    state: 'further_verification_required',
    because: 'Without a confirmed place of operation, waste-law applicability cannot be excluded or confirmed.',
    triggeredBy: ['Emirate of operation: Not sure'],
    unverified: ['Whether waste is generated in the UAE'],
  },
  {
    id: 'env-industrial',
    recordId: 'uae-fl-24-1999-environment',
    when: (p) =>
      uaePresence(p) === 'yes' &&
      (INDUSTRIAL_SECTORS.has(p.sector) || hasActivity(p, 'industrial_operations') || p.multiFacility === 'yes'),
    state: 'potentially_relevant',
    because:
      'Industrial, energy, construction or multi-facility operations in the UAE were indicated. Federal Law No. 24 of 1999 is the framework for pollution control. A permit is activity-specific.',
    triggeredBy: ['Sector, industrial activity or multiple facilities'],
    unverified: ['Whether the activity needs a permit or impact approval from the competent authority'],
  },
  {
    id: 'env-regs',
    recordId: 'uae-cab-37-2001-environment',
    when: (p) =>
      uaePresence(p) === 'yes' &&
      (INDUSTRIAL_SECTORS.has(p.sector) || hasActivity(p, 'industrial_operations') || p.multiFacility === 'yes'),
    state: 'potentially_relevant',
    because: 'The same operational profile makes the executive regulations of the 1999 environment law relevant. Numeric limits were not copied into this tool.',
    triggeredBy: ['Sector, industrial activity or multiple facilities'],
    unverified: ['Which annex applies'],
  },
  {
    id: 'env-unsure',
    recordId: 'uae-fl-24-1999-environment',
    when: (p) => uaePresence(p) === 'not_sure' && (p.sector === 'not_sure' || INDUSTRIAL_SECTORS.has(p.sector)),
    state: 'further_verification_required',
    because: 'Location or the industrial character of the activity is not clear enough to set the environment law aside.',
    triggeredBy: ['Uncertain location or sector'],
    unverified: ['Site activities and permits'],
  },
  {
    id: 'env-regs-unsure',
    recordId: 'uae-cab-37-2001-environment',
    when: (p) => uaePresence(p) === 'not_sure' && (p.sector === 'not_sure' || INDUSTRIAL_SECTORS.has(p.sector)),
    state: 'further_verification_required',
    because: 'The executive regulations follow the environment law. Uncertainty on location or sector keeps them in verification.',
    triggeredBy: ['Uncertain location or sector'],
    unverified: ['Which annex would apply'],
  },
  {
    id: 'mohre-outdoor-mainland',
    recordId: 'uae-mohre-mr-44-2022',
    when: (p) =>
      uaePresence(p) === 'yes' &&
      p.establishment !== 'free_zone' &&
      (hasActivity(p, 'outdoor_work') || OUTDOOR_SECTORS.has(p.sector)),
    state: 'likely_relevant',
    because:
      'Outdoor work or a sector that commonly works in the open was indicated. Article 3 bans work under the sun in open places from 12:30 pm to 3:00 pm between 15 June and 15 September.',
    triggeredBy: ['Outdoor work or outdoor-prone sector'],
    unverified: ['Whether any technically continuous work is exempt under the procedures manual'],
  },
  {
    id: 'mohre-free-zone',
    recordId: 'uae-mohre-mr-44-2022',
    when: (p) =>
      uaePresence(p) === 'yes' &&
      (p.establishment === 'free_zone' || p.establishment === 'not_sure' || p.establishment === 'both') &&
      (hasActivity(p, 'outdoor_work') || OUTDOOR_SECTORS.has(p.sector) || hasActivity(p, 'not_sure')),
    state: 'further_verification_required',
    because:
      'Outdoor work may be in a free zone or the establishment type is not only mainland. Confirm whether those workers are covered by the MOHRE midday-break rule.',
    triggeredBy: ['Establishment type and outdoor work'],
    unverified: ['Employment regime in the free zone'],
  },
  {
    id: 'mohre-unsure',
    recordId: 'uae-mohre-mr-44-2022',
    when: (p) =>
      uaePresence(p) !== 'no' &&
      (p.sector === 'not_sure' || hasActivity(p, 'not_sure') || uaePresence(p) === 'not_sure'),
    state: 'further_verification_required',
    because: 'Sector, activities or location are uncertain, so outdoor-work coverage cannot be excluded.',
    triggeredBy: ['Not sure on sector, activity or location'],
    unverified: ['Whether any work is done under the sun in open places'],
  },
  {
    id: 'c8-bank-insurer',
    recordId: 'cbuae-c-8-2025',
    when: (p) => p.financial === 'bank' || p.financial === 'insurer' || p.sector === 'banking' || p.sector === 'insurance',
    state: 'likely_relevant',
    because: 'Banks and insurance companies are the entities named in Regulation C 8/2025.',
    triggeredBy: ['Bank or insurance company'],
    unverified: ['Group versus solo application, and whether a remediation plan is needed'],
  },
  {
    id: 'c8-other-financial',
    recordId: 'cbuae-c-8-2025',
    when: (p) => p.financial === 'other_financial' || p.sector === 'other_financial',
    state: 'further_verification_required',
    because:
      'The regulation applies to banks and insurers and says the Central Bank may extend it. Other financial firms are not treated here as already in scope.',
    triggeredBy: ['Other financial institution'],
    unverified: ['Whether the firm is a bank or insurer, or has been brought into scope later'],
  },
  {
    id: 'c8-unsure',
    recordId: 'cbuae-c-8-2025',
    when: (p) => p.financial === 'not_sure',
    state: 'further_verification_required',
    because: 'It is not clear whether the entity is a bank or insurer. The regulation cannot be excluded on these answers.',
    triggeredBy: ['Financial-institution question: Not sure'],
    unverified: ['Licence type'],
  },
  {
    id: 'disc-bank',
    recordId: 'cbuae-sustainability-disclosure-2024',
    when: (p) => p.financial === 'bank' || p.financial === 'insurer' || p.sector === 'banking' || p.sector === 'insurance',
    state: 'likely_relevant',
    because:
      'The sustainability disclosure principles are in force for CBUAE reporting entities. Banks and insurers are the core audience. They are principles and do not state a universal filing day.',
    triggeredBy: ['Bank or insurance company'],
    unverified: ['The reporting cycle the supervisor expects from this institution'],
  },
  {
    id: 'disc-other',
    recordId: 'cbuae-sustainability-disclosure-2024',
    when: (p) => p.financial === 'other_financial' || p.financial === 'not_sure' || p.sector === 'other_financial',
    state: 'further_verification_required',
    because: 'Whether this firm is a reporting entity for the disclosure principles is not clear from the answers.',
    triggeredBy: ['Financial status uncertain or other financial services'],
    unverified: ['Reporting-entity status'],
  },
  {
    id: 'principles-bank',
    recordId: 'cbuae-climate-risk-principles-2023',
    when: (p) => p.financial === 'bank' || p.financial === 'insurer' || p.sector === 'banking' || p.sector === 'insurance',
    state: 'likely_relevant',
    because: 'The 2023 climate-risk principles are the supervisory baseline that Regulation C 8/2025 later made more specific for banks and insurers.',
    triggeredBy: ['Bank or insurance company'],
    unverified: [],
  },
  {
    id: 'principles-other',
    recordId: 'cbuae-climate-risk-principles-2023',
    when: (p) => p.financial === 'other_financial' || p.financial === 'not_sure',
    state: 'further_verification_required',
    because: 'The principles sit in the financial-sector framework. Licence type is not confirmed.',
    triggeredBy: ['Financial status'],
    unverified: ['Whether the firm is inside the sustainable-finance framework'],
  },
  {
    id: 'adx-listed',
    recordId: 'adx-esg-guidance',
    when: (p) => listedAdx(p),
    state: 'likely_relevant',
    because: 'ADX listing was indicated. Use the current ADX guidance. This does not confirm a filing day or the separate SCA position.',
    triggeredBy: ['Listing: ADX'],
    unverified: ['Issuer category and any SCA sustainability-report rule'],
  },
  {
    id: 'adx-metrics',
    recordId: 'adx-esg-metrics',
    when: (p) => listedAdx(p),
    state: 'likely_relevant',
    because: 'ADX listing was indicated, so the published metric set is relevant.',
    triggeredBy: ['Listing: ADX'],
    unverified: ['Which indicators the issuer category is expected to use'],
  },
  {
    id: 'adx-ipo',
    recordId: 'adx-esg-guidance',
    when: (p) => p.ipo === 'yes' && !listedAdx(p) && p.listing !== 'dfm',
    state: 'further_verification_required',
    because: 'IPO preparation was indicated without a confirmed ADX listing. The target market has to be confirmed before ADX guidance is treated as applicable.',
    triggeredBy: ['Preparing for an IPO'],
    unverified: ['Target exchange'],
  },
  {
    id: 'adx-metrics-ipo',
    recordId: 'adx-esg-metrics',
    when: (p) => p.ipo === 'yes' && !listedAdx(p) && p.listing !== 'dfm',
    state: 'further_verification_required',
    because: 'IPO preparation may make ADX metrics relevant only if ADX is the target market.',
    triggeredBy: ['Preparing for an IPO'],
    unverified: ['Target exchange'],
  },
  {
    id: 'adx-unsure',
    recordId: 'adx-esg-guidance',
    when: (p) => p.listing === 'not_sure',
    state: 'further_verification_required',
    because: 'Listing status is unknown, so ADX guidance cannot be excluded.',
    triggeredBy: ['Listing: Not sure'],
    unverified: ['Whether any securities are listed on ADX'],
  },
  {
    id: 'adx-metrics-unsure',
    recordId: 'adx-esg-metrics',
    when: (p) => p.listing === 'not_sure',
    state: 'further_verification_required',
    because: 'Listing status is unknown, so the ADX metric set cannot be excluded.',
    triggeredBy: ['Listing: Not sure'],
    unverified: ['Whether any securities are listed on ADX'],
  },
  {
    id: 'dfm-listed',
    recordId: 'dfm-esg-guidance',
    when: (p) => listedDfm(p),
    state: 'likely_relevant',
    because: 'DFM listing was indicated. Confirm the current guide on dfm.ae. No filing day was identified in this dataset.',
    triggeredBy: ['Listing: DFM'],
    unverified: ['Current DFM guide version'],
  },
  {
    id: 'dfm-ipo',
    recordId: 'dfm-esg-guidance',
    when: (p) => p.ipo === 'yes' && !listedDfm(p) && p.listing !== 'adx',
    state: 'further_verification_required',
    because: 'IPO preparation was indicated. DFM guidance matters if DFM is the target market.',
    triggeredBy: ['Preparing for an IPO'],
    unverified: ['Target exchange'],
  },
  {
    id: 'dfm-unsure',
    recordId: 'dfm-esg-guidance',
    when: (p) => p.listing === 'not_sure',
    state: 'further_verification_required',
    because: 'Listing status is unknown, so DFM guidance cannot be excluded.',
    triggeredBy: ['Listing: Not sure'],
    unverified: ['Whether any securities are listed on DFM'],
  },
  {
    id: 'netzero-context',
    recordId: 'uae-net-zero-2050',
    when: (p) => uaePresence(p) === 'yes',
    state: 'voluntary_or_market_driven',
    because: 'The Net Zero 2050 Strategy is national policy context for UAE operations. It is not a company filing duty.',
    triggeredBy: ['Emirate of operation'],
    unverified: [],
  },
  {
    id: 'energy-context',
    recordId: 'uae-energy-strategy-2050',
    when: (p) => uaePresence(p) === 'yes',
    state: 'voluntary_or_market_driven',
    because: 'The Energy Strategy 2050 is national policy context. It does not create a corporate renewable-energy filing.',
    triggeredBy: ['Emirate of operation'],
    unverified: [],
  },
  {
    id: 'cbam-yes',
    recordId: 'eu-cbam-2023-956',
    when: (p) => p.exportEu === 'yes',
    state: 'further_verification_required',
    because:
      'Exports to the EU were indicated. CBAM duties sit with EU importers. Whether the UAE company must supply data depends on the product and the customer. The 31 May 2027 date is the EU declarant deadline, not a UAE filing.',
    triggeredBy: ['Exports to the EU'],
    unverified: ['CN code coverage, who the importer is, and the current consolidated threshold'],
  },
  {
    id: 'cbam-unsure',
    recordId: 'eu-cbam-2023-956',
    when: (p) => p.exportEu === 'not_sure',
    state: 'further_verification_required',
    because: 'EU export exposure is unknown. CBAM cannot be ruled out from these answers.',
    triggeredBy: ['EU exports: Not sure'],
    unverified: ['Whether goods enter the EU'],
  },
  {
    id: 'paris-context',
    recordId: 'paris-agreement',
    when: (p) => uaePresence(p) === 'yes',
    state: 'voluntary_or_market_driven',
    because: 'The Paris Agreement is international context for UAE climate policy. It is not a corporate return.',
    triggeredBy: ['Emirate of operation'],
    unverified: ['Domestic ratification decree number, which is not in this dataset'],
  },
  {
    id: 'ghg-inventory',
    recordId: 'ghg-protocol-corporate',
    when: (p) => p.ghgInventory === 'yes' || p.ghgInventory === 'no' || p.ghgInventory === 'not_sure' || p.customerRequests === 'yes' || p.lenderRequests === 'yes',
    state: 'voluntary_or_market_driven',
    because:
      'The GHG Protocol is a voluntary methodology. It is relevant if the company measures emissions, does not yet have an inventory, or receives data requests. It is not UAE law.',
    triggeredBy: ['GHG inventory answer or stakeholder data requests'],
    unverified: ['Whether any contract names a specific methodology'],
  },
  {
    id: 'gri-market',
    recordId: 'gri-standards',
    when: (p) => p.customerRequests === 'yes' || p.listing === 'adx' || p.listing === 'dfm' || p.listing === 'both_exchanges' || p.ipo === 'yes',
    state: 'voluntary_or_market_driven',
    because: 'GRI is voluntary. Listing, an IPO or customer requests are reasons to check whether a report already claims GRI. They are not a legal duty to use it.',
    triggeredBy: ['Listing, IPO preparation or customer requests'],
    unverified: ['Whether any report or contract requires GRI'],
  },
  {
    id: 'ifrs-market',
    recordId: 'ifrs-s1-s2',
    when: (p) =>
      p.customerRequests === 'yes' ||
      p.lenderRequests === 'yes' ||
      p.listing === 'adx' ||
      p.listing === 'dfm' ||
      p.listing === 'both_exchanges' ||
      p.listing === 'other_exchange' ||
      p.ipo === 'yes' ||
      p.financial === 'bank' ||
      p.financial === 'insurer',
    state: 'voluntary_or_market_driven',
    because:
      'IFRS S1 and S2 are not automatically UAE law. Listing, financing or financial-institution status is a reason to check group or regulator adoption. No UAE adoption decree is recorded in this dataset.',
    triggeredBy: ['Listing, IPO, lender requests or financial institution'],
    unverified: ['Whether a regulator, exchange or parent requires IFRS S1 or S2'],
  },
  {
    id: 'market-requests',
    recordId: 'market-esg-requests',
    when: (p) => p.customerRequests === 'yes' || p.lenderRequests === 'yes',
    state: 'voluntary_or_market_driven',
    because: 'Customer or lender sustainability requests were indicated. They are contractual or commercial, not a UAE statute.',
    triggeredBy: ['Customer or lender requests'],
    unverified: ['The contract clause, frequency and evidence standard'],
  },
  {
    id: 'market-unsure',
    recordId: 'market-esg-requests',
    when: (p) => p.customerRequests === 'not_sure' || p.lenderRequests === 'not_sure',
    state: 'further_verification_required',
    because: 'It is unclear whether counterparties are asking for ESG or emissions data. Uncertainty is not treated as “no requests”.',
    triggeredBy: ['Customer or lender requests: Not sure'],
    unverified: ['Whether procurement or treasury has received a questionnaire'],
  },
]

export const STATE_RANK: Record<ApplicabilityState, number> = {
  further_verification_required: 5,
  likely_relevant: 4,
  potentially_relevant: 3,
  voluntary_or_market_driven: 2,
  no_trigger_identified: 1,
}
